import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { useLanguage } from "../context/LanguageContext";
import { useUI } from "../lib/i18n";

function LanguageSwitcher({ mobile = false }) {
  const { lang, setLang } = useLanguage();
  return (
    <div className="flex items-center gap-1 border border-white/15 rounded-full p-1" data-testid={mobile ? "language-switcher-mobile" : "language-switcher"}>
      {["it", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}-button${mobile ? "-mobile" : ""}`}
          className={`px-2.5 py-1 text-xs font-mono uppercase rounded-full transition-colors ${
            lang === l ? "bg-white text-black" : "text-zinc-400 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const { content } = useContent();
  const ui = useUI();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goToSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const links = [
    { label: ui.nav_services, action: () => goToSection("servizi"), testid: "nav-link-servizi" },
    { label: ui.nav_offers, action: () => goToSection("consulenze"), testid: "nav-link-consulenze" },
    { label: "Palinsesto AI", to: "/palinsesto-ai", testid: "nav-link-palinsesto" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-40 bg-black/60 backdrop-blur-xl border-b border-white/10" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="font-display font-extrabold text-lg tracking-tight flex items-center gap-2" data-testid="nav-brand">
          <span className="w-2 h-2 bg-[#007AFF] rounded-full" />
          {content?.brand || "Studio AI"}
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.to ? (
              <Link key={l.label} to={l.to} data-testid={l.testid}
                className="text-sm text-zinc-400 hover:text-white transition-colors">
                {l.label}
              </Link>
            ) : (
              <button key={l.label} onClick={l.action} data-testid={l.testid}
                className="text-sm text-zinc-400 hover:text-white transition-colors">
                {l.label}
              </button>
            )
          )}
          <button onClick={() => goToSection("contatti")} data-testid="nav-cta-contatti"
            className="rounded-full bg-white text-black px-5 py-2 text-sm font-semibold hover:bg-[#007AFF] hover:text-white transition-colors">
            {ui.nav_contact}
          </button>
          <LanguageSwitcher />
        </nav>

        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-xl"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) =>
                l.to ? (
                  <Link key={l.label} to={l.to} onClick={() => setOpen(false)} data-testid={`${l.testid}-mobile`}
                    className="text-base text-zinc-300 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                ) : (
                  <button key={l.label} onClick={l.action} data-testid={`${l.testid}-mobile`}
                    className="text-left text-base text-zinc-300 hover:text-white transition-colors">
                    {l.label}
                  </button>
                )
              )}
              <div className="flex items-center gap-4">
                <button onClick={() => goToSection("contatti")} data-testid="nav-cta-contatti-mobile"
                  className="rounded-full bg-white text-black px-5 py-3 text-sm font-semibold w-fit">
                  {ui.nav_contact}
                </button>
                <LanguageSwitcher mobile />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
